import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "jalali-moment";

import { commafy, swal, typeChecker } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

const ProductOrdersCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('product_orders'), link: '/product-orders-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	const createProductOrder = async (event) => {
		event.preventDefault();
		if(createButtonIsLoading)
			return 0;
		let adminAnswer = event.target.productOrderAdminAnswer.value.trim();
		if(!typeChecker(adminAnswer, 'title'))
			return toast.warn(t('admin_answer') + ' ' + t('must_have_at_least_3_characters'));
		let status = event.target.productOrderStatus.value;
		let body = {
			order_id: id,
			admin_response: {
				text: adminAnswer
			},
			order_state: status
		};
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			let response = await _post('/orders/admin/' + componentType, body);
			setCreateButtonIsLoading(false);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					setProductOrder({
						...productOrder,
						order_state: parseInt(status)
					});
					if(componentType === 'create')
						event.target.reset();
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		});
	}

	const [productOrder, setProductOrder] = useState({});
	const getProductOrder = async () => {
		let body = {
			order_id: id
		};
		let response = await _post('/orders/admin/getOne', body);
		switch(response.code){
			case 200:
				setProductOrder(response.data.order);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	let orderInformation = productOrder?.order_information;
	if(typeof orderInformation === 'string')
		orderInformation = JSON.parse(orderInformation);
	const orderPayment = orderInformation?.payment;
	const orderProduct = orderInformation?.product || productOrder?.product_id_information;
	const orderPriceUnit = orderPayment?.price_unit || orderProduct?.price_unit || (orderProduct?.productcategory_id_information?.category_type === 2 ? 'zar' : 'sim_zar');
	const orderPriceUnitLabel = productOrder?.order_id ? (orderPriceUnit === 'zar' ? t('score2_name') : `${t('score1_name')} ${t('and')} ${t('score2_name')}`) : '';
	const orderPaymentSummary = orderPayment?.price_unit === 'sim_zar' && orderPayment?.zar_paid > 0
		? `${commafy(orderPayment.sim_paid)} ${t('score1_name')} + ${commafy(orderPayment.zar_paid)} ${t('score2_name')}` + (orderPayment.sim_change > 0 ? ` − ${commafy(orderPayment.sim_change)} ${t('score1_name')} (${t('returned')})` : '')
		: '';

	const productOrderStatusList = [
		{ key: t('pending'), value: 0, disabled: true },
		{ key: t('closed'), value: 1, disabled: [2, 3].includes(productOrder?.order_state) },
		{ key: t('sent'), value: 2, disabled: [1, 3].includes(productOrder?.order_state) },
		{ key: t('failed'), value: 3, disabled: [1, 2].includes(productOrder?.order_state) }
	];
	
	useEffect(() => {
		getProductOrder();
	}, []);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('product_order')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createProductOrder}>
						<div className="row gx-2">
							<FormField fieldKey="orderRegisteryDate" title={t('creation_date')} value={productOrder?.create_date ? moment(productOrder?.create_date?.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD') : ''} disabled ltr />
							<FormField fieldKey="orderUser" title={t('user')} value={productOrder?.user_id_information?.user_fullname} disabled rtl />
							<FormField fieldKey="orderProduct" title={t('product')} value={productOrder?.product_id_information?.product_name} disabled rtl />
							<FormField fieldKey="orderPrice" title={t('price')} value={commafy(orderProduct?.product_price)} disabled ltr />
							<FormField fieldKey="orderPriceUnit" title={t('price_unit')} value={orderPriceUnitLabel} disabled rtl />
							{orderPaymentSummary && <FormField fieldKey="orderPayment" title={t('payment_details')} value={orderPaymentSummary} disabled rtl />}
							<FormField type="select" fieldKey="productOrderStatus" title={t('status')} value={productOrder?.order_state} required selectItems={productOrderStatusList} />
							<FormField type="text" fieldKey="productOrderAdminAnswer" title={t('admin_answer')} value={productOrder?.admin_response?.text} required rtl />
						</div>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading}>
									<i className="fa fa-save"></i> {t('update')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default ProductOrdersCreate;
